/**
 * Exposes the vanilla modules under the global names the Blade views call
 * ($_ui, $_select, confirmDialog, number_format, …) plus the `App` namespace
 * (App.dom, App.http, App.val, App.DataGrid, …) used by the migrated view scripts.
 */
import * as bootstrap from 'bootstrap';
import * as dom from './lib/dom';
import * as http from './lib/http';
import { number_format, number_value, getNumber, setNumber, initNumberInputs } from './lib/number';
import { initSelect, createSelect, setSelectValue, setSelectOptions, destroySelect, offSelect } from './components/Select';
import dayjs from 'dayjs';
import { DataGrid } from './components/DataGrid';
import { initDatePickers, date_format, datetime_format } from './components/DatePicker';
import { DModal, hideModal } from './components/Modal';
import * as dialog from './components/dialog';
import { Tree } from './components/Tree';
import { initLightbox } from './components/Lightbox';
import { SignaturePad, initSignaturePads } from './components/SignaturePad';
import { exportFile, ExportToTable } from './lib/exportTable';
import { val } from './lib/val';
import { initSidebar } from './layout/sidebar';
import { initScrollTop } from './layout/scrolltop';
import { centerAlign, leftAlign, rightAlign } from './layout/print';

// ------------------------------------------------------------------ $_ui

function bindOnce(selector, key, handler) {
    document.querySelectorAll(selector).forEach((el) => {
        if (el.dataset[key]) return;
        el.dataset[key] = '1';
        el.addEventListener('click', handler);
    });
}

/** jQuery's .data() turned "false"/"0"/"" into falsy values; mirror that for data-confirm. */
function confirmDisabled(el) {
    const v = el.dataset.confirm;
    return v !== undefined && (v === 'false' || v === '0' || v === '' || v === 'null');
}

/** Like `$(form).trigger('submit')`: run submit listeners, then submit natively unless prevented. */
function triggerSubmit(form) {
    return dom.submit(form);
}

function onBtnSubmit(e) {
    e.preventDefault();
    const form = this.dataset.form;
    const action = this.dataset.action;
    if (form == null) return window.alert('No form defined');
    const formEl = document.querySelector(form);
    if (!formEl) return window.alert(form + ' Not Found');
    if (action != null) {
        dom.show('#preloader');
        formEl.setAttribute('action', action);
        if (!triggerSubmit(formEl)) dom.hide('#preloader');
    }
}

function onBtnSave(e) {
    e.preventDefault();
    const form = this.dataset.form;
    const action = this.dataset.action;
    const message = this.dataset.message;
    if (form == null) return window.alert('No form defined');
    const formEl = document.querySelector(form);
    if (!formEl) return window.alert(form + ' Not Found');
    if (action != null) formEl.setAttribute('action', action);
    const submit = () => {
        dom.show('.preloader');
        if (!triggerSubmit(formEl)) dom.hide('.preloader');
    };
    if (confirmDisabled(this)) submit();
    else {
        dialog.confirmDialog({
            content: message == null ? 'Anda yakin untuk melanjutkan?' : message,
            yes: submit,
        });
    }
}

function onBtnRemoveRow(e) {
    e.preventDefault();
    const target = this.getAttribute('data-target');
    if (target != null) document.querySelectorAll(target).forEach((el) => el.remove());
    else this.parentElement?.parentElement?.remove();
}

function onBtnDelete(e) {
    e.preventDefault();
    const link = this.dataset.href ?? this.getAttribute('href');
    dialog.deleteDialog(link, this.dataset.message);
}

function onAlertClick() {
    this.style.display = 'none';
}

export function $__plugin() {
    document.querySelectorAll('.nav-link').forEach((el) => el.classList.remove('active'));
    initLightbox();
    initNumberInputs();
}

export function $_init_datepicker() {
    initDatePickers();
}

export function $_init_datetimepicker() {
    initDatePickers();
}

export function $__app() {
    initDatePickers();
    if (window.CKEDITOR && document.querySelector('.rich-text')) {
        window.CKEDITOR.replaceAll('rich-text', { toolbar: [['Source', '-', 'Bold', 'Italic']] });
    }
    bindOnce('.btn-submit', 'uiSubmit', onBtnSubmit);
    bindOnce('.btn-save', 'uiSave', onBtnSave);
    bindOnce('.btn-remove-row', 'uiRemoveRow', onBtnRemoveRow);
    bindOnce('.btn-delete', 'uiDelete', onBtnDelete);
    bindOnce('.alert', 'uiAlert', onAlertClick);
    document.querySelectorAll('[data-bs-toggle="tooltip"], [data-toggle="tooltip"]').forEach((el) => bootstrap.Tooltip.getOrCreateInstance(el));
}

/** Re-scan the page and enhance new elements. Idempotent: safe to call after every render. */
export function $_ui() {
    $__plugin();
    $__app();
    dom.fadeOut('.preloader');
}

// ------------------------------------------------------------------ misc helpers

/** use data-width instead width attribute */
export function $_select2(opt) {
    $_select(opt.id, opt.url, opt.cb, opt.minimum);
}

export function $_select(id, url, cb, minimum) {
    initSelect(id, url, cb, minimum ?? 0);
}

function __generateColor() {
    return '#' + Math.floor(Math.random() * 16777215).toString(16);
}

export function dynamicColorSet(colors, index) {
    if (colors[index] == null) colors[index] = __generateColor();
    return colors;
}

export function defaultColorSet(length) {
    const colors = ['f94144', 'f3722c', 'f8961e', 'f9844a', 'f9c74f', '90be6d', '43aa8b', '4d908e', '577590', '277da1'];
    for (let i = 10; i < length; i++) colors[i] = __generateColor();
    return colors;
}

export function renderWithPermission(row, opt) {
    if (row == null && opt) {
        window.alert("can't render with permission, missing parameter");
        return undefined;
    }
    const wrap = document.createElement('div');
    wrap.innerHTML = row.action;
    const drop = (sel) => wrap.querySelector(sel)?.remove();
    if (opt.delete === '') drop(`#btn_delete_${row.id}`);
    if (opt.update === '') drop(`#btn_edit_${row.id}`);
    if (opt.show === '') drop(`#btn_show_${row.id}`);
    return wrap.innerHTML;
}

// ------------------------------------------------------------------ install

export function installGlobals(win = window) {
    Object.assign(win, {
        bootstrap,
        // $_ui family
        $_ui, $__plugin, $__app, $_init_datepicker, $_init_datetimepicker, $_select, $_select2,
        // dialogs
        DModal,
        confirmDialog: dialog.confirmDialog,
        alert: dialog.alert,
        deleteDialog: dialog.deleteDialog,
        openPopup: dialog.openPopup,
        openWindow: dialog.openWindow,
        closePopup: dialog.closePopup,
        _popup_click: dialog._popup_click,
        popup_new: dialog.popup_new,
        $_popupForm: dialog.$_popupForm,
        showMessage: dialog.showMessage,
        // formatting
        number_format, number_value, date_format, datetime_format,
        // export / print / colours / permissions
        exportFile, ExportToTable, centerAlign, leftAlign, rightAlign,
        dynamicColorSet, defaultColorSet, renderWithPermission,
    });
    // Pages override popup_click with `var popup_click = …`; only provide the default.
    if (typeof win.popup_click !== 'function') win.popup_click = dialog.popup_click;

    // Vanilla API for migrated views (and the future Vue wrappers).
    win.App = {
        dom,
        http,
        DataGrid,
        Tree,
        DModal,
        hideModal,
        getNumber,
        setNumber,
        val,
        each: dom.each,
        dayjs,
        SignaturePad,
        initSignaturePads,
        select: { init: initSelect, create: createSelect, setValue: setSelectValue, setOptions: setSelectOptions, destroy: destroySelect, off: offSelect },
    };

    // `.popup-opener` links open in the iframe window (was bound in dialog.js).
    dom.on(document, 'click', '.popup-opener', function (e) {
        e.preventDefault();
        const href = this.getAttribute('href');
        this.setAttribute('href', '#');
        dialog.openWindow(href);
    });

    dom.ready(() => {
        initSidebar();
        initScrollTop();
        $_ui();
        initSignaturePads();
    });
}
